import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface RejectModalProps {
  isOpen: boolean;
  releaseTitle: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

// Predefined rejection reasons
interface RejectionReason {
  id: string;
  title: string;
  message: string;
}

const REJECTION_REASONS: RejectionReason[] = [
  {
    id: 'track_length',
    title: 'Parça Süresi Yetersiz',
    message: 'The track length is insufficient for distribution. Please submit a track with a minimum length of 1:20 minutes.'
  },
  {
    id: 'audio_quality',
    title: 'Ses Kalitesi / Mastering Eksikliği',
    message: 'The submitted track does not meet audio quality standards. Please provide a properly mastered version.'
  },
  {
    id: 'metadata',
    title: 'Eksik veya Yanlış Metadata',
    message: 'The track metadata (artist name, title, ISRC, etc.) is incomplete or incorrect. Please update and resubmit.'
  },
  {
    id: 'copyright',
    title: 'Telif Hakkı / Hak İhlali',
    message: 'The track appears to contain potential copyright infringement. Only submit original works you own the rights to.'
  },
  {
    id: 'content_policy',
    title: 'Politikalara Uygun Olmayan İçerik',
    message: 'The track cannot be published as it violates our content policies.'
  },
  {
    id: 'cover_art',
    title: 'Kapak Görseli Standart Dışı',
    message: 'The submitted cover artwork does not meet technical requirements (size, resolution, logos, or copyright issues). Please provide a compliant image.'
  },
  {
    id: 'file_format',
    title: 'Format / Dosya Hatası',
    message: 'The track file (WAV, FLAC, etc.) is invalid or corrupted. Please resubmit in the correct format.'
  },
  {
    id: 'duplicate',
    title: 'Yinelenen İçerik (Duplicate Release)',
    message: 'This track already exists on the platform. Duplicate content cannot be published.'
  },
  {
    id: 'missing_ids',
    title: 'Yanlış veya Eksik ID Bilgileri (UPC / ISRC)',
    message: 'The UPC or ISRC code is missing or invalid. Please provide the correct codes.'
  },
  {
    id: 'release_date',
    title: 'Zamanlama Sorunları (Release Date)',
    message: 'The release date provided is invalid or too soon. Please set a release date at least 14 days in advance.'
  },
  {
    id: 'explicit_label',
    title: 'Explicit Content Etiketi Eksik',
    message: 'The track contains explicit content but is not marked accordingly. Explicit works must be correctly labeled.'
  },
  {
    id: 'regional_licensing',
    title: 'Bölgesel Lisanslama Kısıtlamaları',
    message: 'The track cannot be published due to regional licensing restrictions.'
  },
  {
    id: 'name_conflict',
    title: 'Sanatçı İsmi ile Çakışma (Name Conflict)',
    message: 'The artist name conflicts with an existing artist. Please use a distinct name to avoid confusion.'
  },
  {
    id: 'clearance_docs',
    title: 'Eksik Onay Belgeleri (Samples / Remix)',
    message: 'Required clearance documents for samples/remixes are missing. The track cannot be published without proper authorization.'
  },
  {
    id: 'wrong_genre',
    title: 'Yanlış Tür / Kategori',
    message: 'The selected genre/category for the track is incorrect. Please choose an appropriate one and resubmit.'
  }
];

const RejectModal: React.FC<RejectModalProps> = ({
  isOpen,
  releaseTitle,
  onConfirm,
  onCancel
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedReasonId, setSelectedReasonId] = useState<string>('');

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      return;
    }

    setIsSubmitting(true);
    await onConfirm(reason.trim());
    setIsSubmitting(false);
    setReason('');
    setSelectedReasonId('');
  };

  const handleCancel = () => {
    setReason('');
    setSelectedReasonId('');
    onCancel();
  };

  const handleReasonSelect = (reasonId: string) => {
    setSelectedReasonId(reasonId);
    const selectedReason = REJECTION_REASONS.find(r => r.id === reasonId);
    if (selectedReason) {
      setReason(selectedReason.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
      <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Icon ve Başlık */}
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-white">{t('admin.rejectTrack')}</h3>
            </div>
          </div>

          {/* Müzik Adı */}
          <div className="mb-6">
            <p className="text-white/80">
              <span className="font-medium">{t('admin.trackToReject')}:</span>
              <span className="ml-1 text-orange-300">"{releaseTitle}"</span>
            </p>
          </div>

          {/* Hazır Red Sebepleri */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-white mb-2">
              Hazır Red Sebepleri (İsteğe Bağlı)
            </label>
            <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto pr-2">
              {REJECTION_REASONS.map((rejectionReason) => (
                <button
                  key={rejectionReason.id}
                  onClick={() => handleReasonSelect(rejectionReason.id)}
                  disabled={isSubmitting}
                  className={`text-left px-4 py-3 rounded-lg border transition-all duration-200 ${
                    selectedReasonId === rejectionReason.id
                      ? 'bg-orange-500/30 border-orange-500/50 text-white'
                      : 'bg-white/5 border-white/20 text-white/80 hover:bg-white/10 hover:border-white/30'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        selectedReasonId === rejectionReason.id
                          ? 'border-orange-400 bg-orange-500'
                          : 'border-white/40'
                      }`}>
                        {selectedReasonId === rejectionReason.id && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="font-medium text-sm">{rejectionReason.title}</div>
                      <div className="text-xs text-white/60 mt-1">{rejectionReason.message}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <p className="text-xs text-white/60 mt-2">
              Bir sebep seçtiğinizde, mesaj otomatik olarak aşağıdaki alana yazılacaktır. İsterseniz düzenleyebilirsiniz.
            </p>
          </div>

          {/* Manuel Sebep Girişi */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-white mb-2">
              {t('admin.rejectReason')} *
            </label>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                // Clear selected reason if user manually edits
                if (selectedReasonId && e.target.value !== REJECTION_REASONS.find(r => r.id === selectedReasonId)?.message) {
                  setSelectedReasonId('');
                }
              }}
              placeholder={t('admin.rejectReasonPlaceholder')}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
              rows={4}
              maxLength={500}
              disabled={isSubmitting}
            />
            <div className="mt-1 text-xs text-white/60">
              {reason.length}/500 {t('admin.characters')}
            </div>
          </div>

          {/* Uyarı Mesajı */}
          <div className="mb-6 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-200">
                  {t('admin.rejectWarning')}
                </p>
              </div>
            </div>
          </div>

          {/* Butonlar */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={handleCancel}
              disabled={isSubmitting}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-all duration-200 disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !reason.trim()}
              className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-medium transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:transform-none flex items-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('admin.rejecting')}
                </>
              ) : (
                t('admin.rejectTrack')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RejectModal; 